import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { ConfigurableIntegrationType, IInvalidOwnership } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IAddEmployeeResult } from '../../../../models/AdminModels';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { invalidateAccountIntegrationQuery, useRegisterUserMutation, useUserMapMutation } from '../../../../queries';
import { useAccountIntegrationSuspenseQuery } from '../../../../queries/Account';
import { AdminUserViewModel } from '../../../../viewmodels/AdminViewModels';
import { AutoCompleteSearchFieldAccessoryViewMode } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { NewContactIcon } from '../../../../web/components/svgs/icons/NewContactIcon';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { brandPrimary } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { AddEmployeeModal } from '../../user/AddEmployee';
import { IntegrationsManageUsersTableRow } from '../IntegrationsManageUsersTableRow';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	accountId: string;
	integrationType?: ConfigurableIntegrationType;
	styles?: StyleDeclarationValue[];
	invalidOwners?: IInvalidOwnership[];
}

const ALL_KEY = 'All';

const ReassignConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: false,
		representedObject: true,
		title: 'Re-assign',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const UnassignConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: false,
		representedObject: true,
		title: 'Unassign',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const RedtailOwnershipRoleOptions: ISelectOption<Api.RedtailIntegrationOwnershipRole>[] = [
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.FirstObservedUserFromContact,
		id: 'ownership-option-first-observed-user',
		text: 'First Observed User',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.ServicingAdvisor,
		id: 'ownership-option-servicing-advisor',
		text: 'Servicing Advisor',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.ServicingAdvisorRestricted,
		id: 'ownership-option-servicing-advisor-restricted',
		text: 'Servicing Advisor (Mapped Only)',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.WritingAdvisor,
		id: 'ownership-option-writing-advisor',
		text: 'Writing Advisor',
	},
	{
		dataContext: Api.RedtailIntegrationOwnershipRole.WritingAdvisorRestricted,
		id: 'ownership-option-writing-adivsor-restricted',
		text: 'Writing Advisor (Mapped Only)',
	},
];

function _IntegrationsManageUsersTable({ accountId, integrationType }: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('IntegrationsManageUsersTable');

	const impersonationContext = React.useMemo(() => ({ account: { id: accountId } }), [accountId]);
	const queryIntegrationType = Api.isGeneralCsvIntegration(integrationType)
		? Api.ConfigurableIntegrationType.GeneralCsv
		: Api.isMergeAccountingIntegration(integrationType)
			? Api.ConfigurableIntegrationType.MergeAccounting
			: Api.isMergeCrmIntegration(integrationType)
				? Api.ConfigurableIntegrationType.MergeCrm
				: integrationType;

	const { data: integration } = useAccountIntegrationSuspenseQuery({
		impersonationContext,
		meta: {
			errorLogging: {
				category: 'IntegrationsManageUsersTable',
				action: 'AccountIntegrationQuery-Error',
			},
		},
		type: queryIntegrationType,
	});

	const userMapMutation = useUserMapMutation({
		impersonationContext: { account: { id: accountId } },
		onError: error => {
			logApiError('AssignMapping-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			invalidateAccountIntegrationQuery({ impersonationContext, type: queryIntegrationType });
		},
	});
	const registerUserMutation = useRegisterUserMutation({
		onError: error => {
			logApiError('CreateUser-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const [selectedIntegrationUser, setSelectedIntegrationUser] = React.useState<Api.IIntegrationUserMapping>();
	const [selectedLevitateUser, setSelectedLevitateUser] = React.useState<AdminUserViewModel>();
	const [showReassignConfirmation, setShowReassignConfirmation] = React.useState(false);
	const [isShowingAddEmployeeModal, setIsShowingAddEmployeeModal] = React.useState(false);
	const [isEditingTable, setIsEditingTable] = React.useState(false);
	const [filteredConnectionType, setFilteredConnectionType] = React.useState(null);
	const [pendingUserMap, setPendingUserMap] = React.useState<Record<string, string>>({});
	const hasPendingMappingChanges = Object.keys(pendingUserMap).length > 0;
	const connectedLevIds = React.useMemo(
		() =>
			integration.integrationUserConfigurations
				? integration.integrationUserConfigurations.filter(conf => conf.isConnected).map(conf => conf.levitateUserId)
				: [],
		[integration.integrationUserConfigurations]
	);

	const onEditTable = () => {
		setIsEditingTable(true);
	};

	const addToPendingUserMap = (user: Api.IIntegrationUserMapping, levitateUserId: string | null) => {
		// If the user is already mapped to the same Levitate user, remove it from the pending map
		let newPendingUserMap: Record<string, string>;
		if ((levitateUserId == null && user.levitateUser?.id == null) || user.levitateUser?.id === levitateUserId) {
			const { [user.integrationId]: _, ...rest } = pendingUserMap;
			newPendingUserMap = rest;
		} else {
			newPendingUserMap = { ...pendingUserMap, [user.integrationId]: levitateUserId };
		}
		setPendingUserMap(newPendingUserMap);
	};
	const bulkSave = () => {
		const userMap = integration.userMap?.slice() ?? [];
		const pendingIntegrationIds = new Set(Object.keys(pendingUserMap));
		for (const userMapEntry of userMap) {
			if (pendingIntegrationIds.has(userMapEntry.integrationId)) {
				const pendingUserMapEntry = pendingUserMap[userMapEntry.integrationId];
				userMapEntry.levitateUser = pendingUserMapEntry != null ? { id: pendingUserMapEntry } : null;
			}
		}
		userMapMutation.mutate({ type: queryIntegrationType, userMap });
		setPendingUserMap({});
		setIsEditingTable(false);
	};

	const onEditTableCancel = () => {
		setPendingUserMap({});
		setIsEditingTable(false);
	};

	const toggleReassingConfirmationDialog = (_showReassignConfirmation: boolean) => {
		if (!_showReassignConfirmation) {
			setSelectedIntegrationUser(null);
			setSelectedLevitateUser(null);
		}
		setShowReassignConfirmation(_showReassignConfirmation);
	};

	const assignUser = async (user: Api.IIntegrationUserMapping, levitateUser?: AdminUserViewModel | Api.IUser) => {
		const userMap = integration.userMap?.slice() ?? [];
		const mapping = userMap.find(x => x.integrationId === user.integrationId);
		if (mapping) {
			mapping.levitateUser = levitateUser ? { id: levitateUser.id } : null;
			logEvent('AssignMapping', mapping);
			userMapMutation.mutate({ type: queryIntegrationType, userMap });
		}
		setSelectedIntegrationUser(null);
		setSelectedLevitateUser(null);
	};

	const onAddEmployee = () => {
		setSelectedIntegrationUser(null);
		setIsShowingAddEmployeeModal(true);
	};
	const onAddEmployeeModalRequestClose = async (result?: IAddEmployeeResult, cancel?: boolean) => {
		setIsShowingAddEmployeeModal(false);
		setSelectedIntegrationUser(null);
		if (!cancel && !!result && !!result.user) {
			await registerUserMutation.mutateAsync({
				accountId,
				sendWelcomeEmail: result.sendWelcomeEmail,
				user: result.user,
			});
			if (selectedIntegrationUser) {
				assignUser(selectedIntegrationUser, result.user);
			}
		}
	};
	const onReassignConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!!result && !canceled && !!result.representedObject) {
			assignUser(selectedIntegrationUser, selectedLevitateUser);
		}
		toggleReassingConfirmationDialog(false);
	};
	const onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		suggestedViewModel.addParam({ includeDeactivated: false });
		return suggestedViewModel.impersonate({
			account: { id: accountId },
		});
	};
	const onUserSelected =
		(user: Api.IIntegrationUserMapping) => (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>) => {
			if (e.target) {
				e.target.setSearchQuery(getDisplayName(e.selection));
			}
			const levUser = new Api.UserViewModel(userSession, e.selection);
			addToPendingUserMap(user, levUser.id);
		};

	const uniqueConnectionTypes: string[] = React.useMemo(() => {
		if (!integration.userMap) {
			return [ALL_KEY];
		}

		const allConnectionTypeArrays = integration.userMap.map(user => {
			return user.connectionTypes;
		});
		const flattenedConnectionTypes: string[] = allConnectionTypeArrays.flat();
		const distinctConnectionTypes = flattenedConnectionTypes.filter(
			(value, index, array) => array.indexOf(value) === index
		);

		return [ALL_KEY, ...distinctConnectionTypes];
	}, [integration]);

	const getOwnershipRole = () => {
		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360: {
				const ams = integration as Api.IAms360Integration;
				return ams.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.HawkSoft: {
				const hawksoft = integration as Api.IHawkSoftIntegration;
				return hawksoft.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Eclipse: {
				const eclipse = integration as Api.IEclipseIntegration;
				return eclipse.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Epic: {
				const epic = integration as Api.IEpicCsvIntegration;
				return epic.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Redtail: {
				const option =
					(integration as Api.IRedtailIntegration).ownershipRole?.toString() ||
					Api.RedtailIntegrationOwnershipRole.FirstObservedUserFromContact;
				return RedtailOwnershipRoleOptions.find(x => x.dataContext === option)?.text || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.QQ: {
				const qq = integration as Api.IQQIntegration;
				return qq.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Xanatek: {
				const xanatek = integration as Api.IGeneralCsvIntegration;
				return xanatek.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.Wealthbox: {
				const wealthbox = integration as Api.IWealthboxIntegration;
				return wealthbox.ownershipRole?.toString() || 'Unknown';
			}
			case Api.ConfigurableIntegrationType.DonorPerfect: {
				const donorPerfect = integration as Api.IDonorPerfectIntegration;
				return donorPerfect.ownershipRole?.toString() || 'Unknown';
			}
			default: {
				return null;
			}
		}
	};

	const hasOwnershipLock = Boolean(integration.locks?.ownershipRoleLock);

	const onConnectionTypeFilterChanged = (value: string) => {
		setFilteredConnectionType(value === ALL_KEY ? null : value);
	};

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.actionToolbar)}>
				<div>Mapping By: {getOwnershipRole()}</div>

				<div className={css(styleSheet.actions)}>
					<span className={css(styleSheet.dropdownContainer)}>
						<div className={css(styleSheet.dropdownText)}>Browse Type:</div>
						<Select value={filteredConnectionType ?? ALL_KEY} onValueChange={onConnectionTypeFilterChanged}>
							<SelectTrigger>
								<span>{filteredConnectionType ?? ALL_KEY}</span>
							</SelectTrigger>
							<SelectContent>
								{uniqueConnectionTypes.map(name => (
									<SelectItem key={name} value={name}>
										{name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</span>

					<button
						className={`${css(styleSheet.tableHeaderEditHeaderEditButton)}`}
						disabled={hasOwnershipLock}
						onClick={onAddEmployee}
					>
						<NewContactIcon fillColor={brandPrimary} className={css(styleSheet.tableHeaderEditHeaderEditButtonIcon)} />
						<span>Add Employee</span>
					</button>
					{isEditingTable ? (
						<>
							<button
								className={css(baseStyleSheet.ctaButtonSmall)}
								disabled={!hasPendingMappingChanges}
								onClick={bulkSave}
							>
								<span>Save</span>
							</button>
							<button className={css(baseStyleSheet.ctaButtonReverseSmall)} onClick={onEditTableCancel}>
								<span>Cancel</span>
							</button>
						</>
					) : (
						<>
							<button
								className={`${css(styleSheet.tableHeaderEditHeaderEditButton)}`}
								disabled={hasOwnershipLock}
								onClick={onEditTable}
							>
								<EditItemPenIcon className={css(styleSheet.tableHeaderEditHeaderEditButtonIcon)} />
								<span>Edit</span>
							</button>
						</>
					)}
				</div>
			</div>
			<div className={css(styleSheet.tableContainer, hasOwnershipLock && styleSheet.tableDisabled)}>
				<table className={css(styleSheet.container)}>
					<thead className={css(styleSheet.tableHeader)}>
						<tr className={css(styleSheet.tableHeaderRow)}>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderInitialsHeader)}>
								{integrationType === ConfigurableIntegrationType.Redtail
									? 'Advisors'
									: integrationType === ConfigurableIntegrationType.QQ ||
											integrationType === ConfigurableIntegrationType.Wealthbox ||
											integrationType === ConfigurableIntegrationType.DonorPerfect ||
											Api.isMergeAccountingIntegration(integrationType) ||
											Api.isMergeCrmIntegration(integrationType)
										? 'Name'
										: 'Initials'}
							</th>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderEmailHeader)}>Type</th>
							<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderEmailHeader)}>Levitate User</th>
							{Api.isMultiUserIntegration(integrationType) ? (
								<>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderStatusIndicatorHeader)} />
									<th className={css(styleSheet.tableHeaderCell, styleSheet.tableHeaderStatusHeader)}>Status</th>
								</>
							) : null}
						</tr>
					</thead>
					<tbody>
						{(integration.userMap ?? []).map(x => {
							if (!x.integrationId) {
								return null;
							}
							if (filteredConnectionType && x.connectionTypes?.indexOf(filteredConnectionType) === -1) {
								return null;
							}
							const connectionTypes = x?.connectionTypes ? x?.connectionTypes.sort((a, b) => a.localeCompare(b)) : null;
							return (
								<IntegrationsManageUsersTableRow
									connectedLevIds={connectedLevIds}
									key={x.integrationId}
									integrationUser={x}
									showConnectionStatusColumns={Api.isMultiUserIntegration(integrationType)}
								>
									<td className={css(styleSheet.cell)}>{connectionTypes?.join(', ')}</td>
									<td className={css(styleSheet.cell)}>
										{isEditingTable ? (
											<SimpleAutoCompleteSearchField
												key={x.integrationId}
												dropdownContentItemsStyles={[styleSheet.autoCompleteDropdownItems]}
												dropdownContentStyle={styleSheet.autoCompleteDropdownContent}
												onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
												// FOLLOWUP: Resolve

												// @ts-ignore
												onItemSelected={onUserSelected(x)}
												resultsLimit={5}
												pageSize={5}
												placeholder='Search name'
												style={styleSheet.emailInput}
												type={Api.ResourceAutoCompleteViewModelType.User}
												rightAccessoryViewMode={AutoCompleteSearchFieldAccessoryViewMode.Always}
												onClear={() => addToPendingUserMap(x, null)}
												initialSearchQuery={x.levitateUser?.id ? getDisplayName(x.levitateUser) : ''}
											/>
										) : x.levitateUser?.id ? (
											getDisplayName(x.levitateUser)
										) : null}
									</td>
								</IntegrationsManageUsersTableRow>
							);
						})}
					</tbody>
					<AddEmployeeModal
						modalProps={{
							isOpen: isShowingAddEmployeeModal,
							onRequestClose: onAddEmployeeModalRequestClose,
						}}
					/>
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{
							isOpen: !!showReassignConfirmation,
							onRequestClose: onReassignConfirmationRequestClose,
						}}
						options={!selectedLevitateUser ? UnassignConfirmationOptions : ReassignConfirmationOptions}
						title={`Are you sure you want to ${
							selectedLevitateUser
								? `re-assign all client information to ${selectedLevitateUser.name}`
								: 'remove this assignment'
						}?`}
					/>
				</table>
			</div>
		</div>
	);
}

export const IntegrationsManageUsersTable = observer(_IntegrationsManageUsersTable);
